<template>
  <!-- DOMAIN CTAs -->
  <span class="cmp-ctas" :class="{ mobile, desktop: !mobile }">
    <div
      v-if="loading"
      class="text-center"
      :class="{ 'full-width': mobile, 'q-mr-md': !mobile }"
    >
      <ws-spinner
        :height="mobile ? '24px' : undefined"
        :width="mobile ? '64px' : undefined"
        :color="inCart ? 'dark' : !inCart && isTheme(Theme.LOOPIA) ? 'white' : undefined"
      />
    </div>

    <ws-button
      v-else-if="workflow === CTAS_WORKFLOW.BUY"
      :type="WsButtonEnum.Type.PRIMARY"
      :size="WsButtonEnum.Size.XL"
      class="cy-buy-domain"
      :class="{
        error,
        mobile,
      }"
      icon="cart"
      @click="buy()"
    >
      <span v-if="!mobile" class="block">{{
        $t(
          error
            ? 'domain_checker.buy_domain.failed'
            : 'domain_checker.buy_domain.implicit'
        )
      }}</span>
    </ws-button>

    <!-- REMOVE FROM CART button -->
    <ws-button
      v-else-if="workflow === CTAS_WORKFLOW.IN_CART"
      class="remove"
      :class="{
        error,
        mobile,
      }"
      icon="trash"
      @click="bundle ? removeBundle() : removeDomain()"
    >
      <span v-if="!mobile" class="block">{{
        $t(
          `domain_checker.${transferInCart ? 'transfer.' : ''}remove_domain.${
            error ? 'failed' : 'implicit'
          }`
        )
      }}</span>
    </ws-button>

    <!-- TODO: WATCH DOMAIN -->
    <!--<ws-button
      v-if="
        !get(domain, 'availableForPurchase', false) &&
          !get(domain, 'error', null)
      "
      class="mobile-smaller"
      :class="{ error: false, mobile }"
      icon="mail"
      @click="showTransfer = !showTransfer"
      >{{
       $t(mobile ? 'domain_checker.watch_domain.button_text_mobile' : 'domain_checker.watch_domain.button_text_desktop')
      }}
    </ws-button>-->

    <!-- IS YOURS -->
    <span v-else-if="workflow === CTAS_WORKFLOW.YOURS">
      <ws-button class="mobile-smaller" icon="info">{{
        $t(
          mobile
            ? 'domain_checker.i_am_owner.label_mobile'
            : 'domain_checker.i_am_owner.label'
        )
      }}
      </ws-button>
      <ws-tooltip :delay="500">{{ $t('domain_checker.i_am_owner.tooltip') }}</ws-tooltip>
    </span>

    <!-- TAKEN -->
    <span v-else-if="workflow === CTAS_WORKFLOW.TAKEN">
      <ws-button
        class="mobile-smaller without-separator"
        icon="alert_triangle"
        :label="$t(
          mobile
            ? 'domain_checker.domain_is_taken.label_mobile'
            : 'domain_checker.domain_is_taken.label'
        )"
      />
      <ws-tooltip :delay="500">{{ $t('domain_checker.domain_is_taken.tooltip') }}</ws-tooltip>
    </span>

    <!-- MY DOMAIN - transfer screen -->
    <span
      v-else-if="workflow === CTAS_WORKFLOW.MY_DOMAIN"
      ref="myDomain"
      class="my-domain"
      :class="{ active: isMyDomainPopup }"
    >
      <ws-button
        ref="myDomainButton"
        class="mobile-smaller"
        :class="{ mobile }"
        icon="domain"
      >{{
        $t(
          mobile
            ? 'domain_checker.my_domain.btn_mobile'
            : 'domain_checker.my_domain.btn_desktop'
        )
      }}</ws-button>

      <domain-popup
        ref="domainPopup"
        v-model="domain"
        @show="isMyDomainPopup = true"
        @hide="isMyDomainPopup = false"
      >
        <domain-popup-transfer
          :domain="domain"
          :should-login="shouldLogin"
          @hide-popup="hidePopup"
        />
      </domain-popup>
    </span>
  </span>
</template>

<script lang="ts">
import {Component, Inject, Prop, Vue} from 'vue-property-decorator';
import {mapGetters} from 'vuex';

import {get} from 'lodash-es';
import { WsButton, WsButtonEnum } from '@loopia-group/components-vue2';
import {StateMutations} from '@/store/const.enum';
import type {Domain, DomainBundle} from '@/components/domain-checker/dc.types';
import DomainPopup from './DomainPopup.vue';
import DomainPopupTransfer from './DomainPopupTransfer.vue';
import {
  addBundleToCart,
  addDomainToCart,
  removeBundleFromCart,
  removeDomainFromCart,
} from '@/services/domain-checker.service';
import {Theme} from '@loopia-group/services';
import {CTAS_WORKFLOW} from './Ctas.enum';
import WsSpinner from '@WS_Components/WsSpinner.vue';
import { WsTooltip } from '@loopia-group/components-vue2';

@Component({
  components: {
    WsSpinner,
    WsTooltip,
    WsButton,
    DomainPopup,
    DomainPopupTransfer,
  },

  computed: {
    ...mapGetters(['cartDomains', 'loggedIn']),
  },
})
export default class Ctas extends Vue {
  @Prop()
    domain!: Domain;
  // OR:
  @Prop({ default: undefined }) bundle!: DomainBundle | undefined;
  @Prop(Boolean) readonly transferInCart!: boolean;
  @Prop(Boolean) readonly inCart!: boolean;
  @Prop(Boolean) readonly loading!: boolean;
  @Prop(Boolean) readonly error!: boolean;
  @Prop({ type: String, default: 'domainChecker.general' })
  readonly path!: string;
  @Inject({ from: 'onBoughtDomain', default: () => () => {} })
  readonly onBoughtDomain!: (d: Domain) => void;
  readonly loggedIn!: boolean;
  readonly cartDomains!: string[];
  Theme = Theme;

  isMyDomainPopup = false;
  showTransfer = false;

  CTAS_WORKFLOW = CTAS_WORKFLOW;
  WsButtonEnum = WsButtonEnum;

  get workflow() {
    switch (true) {
    case this.inCart:
      return CTAS_WORKFLOW.IN_CART;

    case this.availableForTransfer:
      return CTAS_WORKFLOW.MY_DOMAIN;

    case !this.bundle && !this.availableForPurchase:
      return this.isOwnerOfDomain ? CTAS_WORKFLOW.YOURS : CTAS_WORKFLOW.TAKEN;

    default:
      return CTAS_WORKFLOW.BUY;
    }
  }

  get shouldLogin(): boolean {
    return this.domainAlreadyAssigned && !this.loggedIn;
  }

  // Flags ==============
  get availableForPurchase(): boolean {
    return !!this.domain?.availableForPurchase;
  }

  get availableForTransfer(): boolean {
    return !!this.domain?.availableForTransfer;
  }

  get isOwnerOfDomain(): boolean {
    return !!this.domain?.isOwnerOfDomain;
  }

  get domainAlreadyAssigned(): boolean {
    return !!this.domain?.domainAlreadyAssigned;
  }

  public buy() {
    if (this.loading) {
      return;
    }
    this.bundle ? this.buyBundle() : this.buyDomain();
  }

  buyDomain(domain?: Domain) {
    domain = domain || this.domain;
    this.setDomainItemLoading(domain!.name, true);

    addDomainToCart(domain)
      .then(() => this.onBoughtDomain(domain!))
      .catch(this.handleCartApiError)
      .finally(() => this.setDomainItemLoading(domain?.name, false));
  }

  removeDomain() {
    const domain = this.domain;
    this.setDomainItemLoading(domain.name, true);
    removeDomainFromCart(domain)
      .catch(this.handleCartApiError)
      .finally(() => {
        this.setDomainItemLoading(domain.name, false);
      });
  }

  hidePopup() {
    (this.$refs.domainPopup as any).hide();
  }

  get mainDomainInCart(): boolean {
    return this.bundle
      ? this.cartDomains.includes(this.bundle.mainDomain.name)
      : false;
  }

  get discountedDomainInCart(): boolean {
    return this.bundle
      ? this.cartDomains.includes(this.bundle.discountedDomain.name)
      : false;
  }

  /*
    Domain Bundles
   */
  buyBundle() {
    // First or second domain in cart? Add to cart only the domain that is not in cart yet.
    if (this.mainDomainInCart && !this.discountedDomainInCart) {
      this.buyDomain(this.bundle!.discountedDomain);
      return;
    } else if (this.discountedDomainInCart && !this.mainDomainInCart) {
      this.buyDomain(this.bundle!.mainDomain);
      return;
    }

    this.setDomainItemLoading(this.bundle!.mainDomain.name, true);
    this.setDomainItemLoading(this.bundle!.discountedDomain.name, true);

    addBundleToCart(this.bundle!)
      .catch(this.handleCartApiError)
      .finally(() => {
        this.setDomainItemLoading(this.bundle?.mainDomain?.name, false);
        this.setDomainItemLoading(this.bundle?.discountedDomain?.name, false);
      });
  }

  removeBundle() {
    if (!this.bundle) {
      return;
    }

    this.setDomainItemLoading(this.bundle.mainDomain.name, true);
    this.setDomainItemLoading(this.bundle.discountedDomain.name, true);

    removeBundleFromCart(this.bundle)
      .catch(this.handleCartApiError)
      .finally(() => {
        this.setDomainItemLoading(this.bundle?.mainDomain?.name || '', false);
        this.setDomainItemLoading(
          this.bundle?.discountedDomain?.name || '',
          false
        );
      });
  }

  setDomainItemLoading(domainName: string = '', loading: boolean) {
    if (loading) {
      // clear messages when loading started
      this.$messageService.clearMessages(this.path);
    }

    this.$store.commit(StateMutations.SET_DOMAIN_ITEM_LOADING_STATE, {
      domainName,
      loading,
    });
  }

  handleCartApiError(error: any) {
    if (get(error, 'key') === 'exception.session.lock_timeout') {
      // ignore timeouts, do not show error message, error state of UI will be enough
      return;
    } else {
      this.$messageService.errorHandler(this.path)(error);
    }
  }
}
</script>

<style lang="scss" scoped>
@mixin remove-focus-helper() {
  .q-focus-helper,
  .q-focusable:focus .q-focus-helper,
  .q-hoverable:active .q-focus-helper,
  .q-hoverable:hover .q-focus-helper {
    opacity: 0 !important;
    transition: unset !important;
  }
}

.cmp-ctas {
  max-width: 100%;
  display: inline-block;
  position: relative;

  .ws-button {
    max-width: 100%;

    ::v-deep {
      @include remove-focus-helper;

      span,
      svg {
        transition: color $element-hover-animation-speed
          $element-hover-animation-type;
      }
    }

    &.error,
    &.error:hover {
      ::v-deep {
        span {
          color: $ws-red;
        }
        svg {
          fill: $ws-red;
        }
      }
    }

    &.q-btn--flat > ::v-deep .q-btn__wrapper {
      padding-right: map-get($space-md, y);
    }
  }

  &.mobile .ws-button.q-btn--flat > ::v-deep .q-btn__wrapper {
    padding-right: 0;
  }

  .my-domain.active ::v-deep &.ws-button.q-btn .q-btn__content {
    color: $ws-quaternary-neon;
  }
}
</style>
